import React, { useState, useContext, useCallback } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as Yup from 'yup';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  DialogActions,
} from '@material-ui/core';
import { Formik } from 'formik';

import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { ArchivalUnitReservation, PrivateArchivalUnit } from 'types';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import {
  requestExtensionForPrivateArchivalUnit,
  requestExtensionForArchivalUnitReservation,
} from './lent-api';
import { LentTableRefreshCtx } from './lent-table-refresh-ctx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    dialog: {
      width: 350,
    },
    dialogTitle: {
      fontWeight: 'bold',
    },
    dialogActions: {
      marginTop: 20,
      padding: '8px 24px',
    },
    error: {
      color: 'red',
      fontSize: 12,
      paddingTop: 10,
      textAlign: 'right',
    },
  })
);

async function requestExtension(
  archivalUnitType: 'PrivateArchivalUnit' | 'ArchivalUnitReservation',
  itemId: string,
  expirationDate: string
): Promise<{ ok: boolean; data: any }> {
  try {
    if (archivalUnitType === 'PrivateArchivalUnit') {
      await requestExtensionForPrivateArchivalUnit(itemId, expirationDate);
    } else {
      await requestExtensionForArchivalUnitReservation(itemId, expirationDate);
    }

    return {
      ok: true,
      data: {},
    };
  } catch (e) {
    console.log(e);

    return {
      ok: false,
      data: e,
    };
  }
}

interface ExtensionCellFactoryProps {
  archivalUnitType: 'PrivateArchivalUnit' | 'ArchivalUnitReservation';
}

export function extensionCellFactory({
  archivalUnitType,
}: ExtensionCellFactoryProps) {
  return function ExtensionCellComponent({
    data,
    allData,
  }: {
    data: any;
    allData: any;
  }) {
    const classes = useStyles();
    const { showNotification } = useContext(NotificationCtx);
    const { refreshTablePage } = useContext(LentTableRefreshCtx);
    const [openedDialog, setOpenedDialog] = useState<boolean>(false);
    const row = allData as PrivateArchivalUnit | ArchivalUnitReservation;

    const { id, requestedExtensionTo, expiration } = row;
    const hasExtension = requestedExtensionTo != null;

    const handleOpenDialog = () => setOpenedDialog(true);
    const handleCloseDialog = () => setOpenedDialog(false);

    const handleRequestExtension = useCallback(
      async (values: { extensionDate: string }) => {
        const response = await requestExtension(
          archivalUnitType,
          id,
          values.extensionDate
        );

        if (response.ok) {
          showNotification(
            'Žádost o prodloužení vypůjčení byla úspěšně odeslána',
            NotificationVariant.SUCCESS
          );

          await refreshTablePage();

          handleCloseDialog();
        } else {
          showNotification(
            `Žádost o prodloužení vypůjčení se nepodařilo odeslat.
        
            Chyba: ${response.data?.message ?? '-'}`,
            NotificationVariant.ERROR
          );
        }
      },
      [id, refreshTablePage, showNotification]
    );

    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);

    const extendedExpiration = requestedExtensionTo
      ? requestedExtensionTo
      : futureDate.toISOString().substring(0, 10);

    return hasExtension ? (
      <Typography component="span">
        {new Date(extendedExpiration).toLocaleDateString('cs')}
      </Typography>
    ) : (
      <>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleOpenDialog}
        >
          Prodloužit
        </Button>

        <Dialog open={openedDialog}>
          <Grid className={classes.dialog}>
            <Formik
              initialValues={{ extensionDate: extendedExpiration }}
              onSubmit={handleRequestExtension}
              validationSchema={Yup.object().shape({
                extensionDate: Yup.string().test('extensionDate', '', function (
                  value
                ) {
                  const { createError, path } = this;

                  if (value != null && expiration != null) {
                    if (new Date(expiration) >= new Date(value)) {
                      return createError({
                        path,
                        message:
                          'Datum prodloužení musí být větší než původní datum expirace.',
                      });
                    }
                    if (new Date(value) > new Date(futureDate)) {
                      return createError({
                        path,
                        message: 'Prodloužení o více než 30 dní není možné.',
                      });
                    }
                  }
                  return true;
                }),
              })}
            >
              {({ handleSubmit, errors }) => (
                <>
                  <DialogTitle>
                    <Typography
                      component="span"
                      className={classes.dialogTitle}
                    >
                      Žádost o prodloužení rezervace
                    </Typography>
                  </DialogTitle>
                  <DialogContent>
                    <FormItemTextField
                      name="extensionDate"
                      label="Prodloužit do"
                      type="date"
                      disabled={false}
                    />
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    <Button
                      color="primary"
                      onClick={() => handleSubmit()}
                      disabled={!!errors.extensionDate}
                    >
                      Potvrdit
                    </Button>
                    <Button onClick={handleCloseDialog}>Zavřít</Button>
                  </DialogActions>
                </>
              )}
            </Formik>
          </Grid>
        </Dialog>
      </>
    );
  };
}
